/**
 * Plugin Log Store — Wave 22.13.9
 *
 * Per-plugin logging isolation via a Zustand store.
 * Provides plugin-scoped logs, warnings, errors, performance metrics,
 * capability denials, and manifest validation errors.
 *
 * Exposed via DevTools Plugin Stability Dashboard.
 */

import { create } from 'zustand';

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export type PluginLogLevel = 'debug' | 'info' | 'warn' | 'error' | 'perf';

export interface PluginLogEntry {
  id: string;
  pluginId: string;
  level: PluginLogLevel;
  message: string;
  context?: string;
  detail?: Record<string, unknown>;
  timestamp: string;
}

export interface PluginLogChannel {
  pluginId: string;
  logs: PluginLogEntry[];
  warnings: PluginLogEntry[];
  errors: PluginLogEntry[];
  perfMetrics: PluginLogEntry[];
  capabilityDenials: PluginLogEntry[];
  manifestErrors: PluginLogEntry[];
}

// ────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────

const MAX_LOGS_PER_CHANNEL = 200;

let _nextLogId = 1;
function nextLogId(): string {
  return `plog-${Date.now()}-${_nextLogId++}`;
}

// ────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────

interface PluginLogState {
  channels: Map<string, PluginLogChannel>;

  /** Append a log entry to a plugin's channel. */
  appendLog(pluginId: string, level: PluginLogLevel, message: string, context?: string, detail?: Record<string, unknown>): PluginLogEntry;

  /** Record a capability denial. */
  recordCapabilityDenial(pluginId: string, capability: string, detail?: Record<string, unknown>): void;

  /** Record a manifest validation error. */
  recordManifestError(pluginId: string, error: string, detail?: Record<string, unknown>): void;

  /** Record a perf metric. */
  recordPerfMetric(pluginId: string, metric: string, durationMs: number, detail?: Record<string, unknown>): void;

  /** Get channel for a plugin. */
  getChannel(pluginId: string): PluginLogChannel;

  /** Get all channels. */
  getAllChannels(): PluginLogChannel[];

  /** Get logs for a plugin by level. */
  getLogs(pluginId: string, level?: PluginLogLevel, limit?: number): PluginLogEntry[];

  /** Clear logs for a plugin. */
  clearPlugin(pluginId: string): void;

  /** Full reset. */
  reset(): void;
}

function createEmptyChannel(pluginId: string): PluginLogChannel {
  return {
    pluginId,
    logs: [],
    warnings: [],
    errors: [],
    perfMetrics: [],
    capabilityDenials: [],
    manifestErrors: [],
  };
}

function trimLogs(entries: PluginLogEntry[]): PluginLogEntry[] {
  return entries.length > MAX_LOGS_PER_CHANNEL
    ? entries.slice(-MAX_LOGS_PER_CHANNEL)
    : entries;
}

export const usePluginLogStore = create<PluginLogState>((set, get) => ({
  channels: new Map(),

  appendLog(pluginId, level, message, context, detail) {
    const entry: PluginLogEntry = {
      id: nextLogId(),
      pluginId,
      level,
      message,
      context,
      detail,
      timestamp: new Date().toISOString(),
    };

    set((state) => {
      const channels = new Map(state.channels);
      const channel = { ...(channels.get(pluginId) ?? createEmptyChannel(pluginId)) };

      channel.logs = trimLogs([...channel.logs, entry]);

      switch (level) {
        case 'warn':
          channel.warnings = trimLogs([...channel.warnings, entry]);
          break;
        case 'error':
          channel.errors = trimLogs([...channel.errors, entry]);
          break;
        case 'perf':
          channel.perfMetrics = trimLogs([...channel.perfMetrics, entry]);
          break;
      }

      channels.set(pluginId, channel);
      return { channels };
    });

    return entry;
  },

  recordCapabilityDenial(pluginId, capability, detail) {
    const entry: PluginLogEntry = {
      id: nextLogId(),
      pluginId,
      level: 'warn',
      message: `Capability denied: ${capability}`,
      context: 'capability-enforcement',
      detail,
      timestamp: new Date().toISOString(),
    };

    set((state) => {
      const channels = new Map(state.channels);
      const channel = { ...(channels.get(pluginId) ?? createEmptyChannel(pluginId)) };
      channel.capabilityDenials = trimLogs([...channel.capabilityDenials, entry]);
      channel.logs = trimLogs([...channel.logs, entry]);
      channels.set(pluginId, channel);
      return { channels };
    });
  },

  recordManifestError(pluginId, error, detail) {
    const entry: PluginLogEntry = {
      id: nextLogId(),
      pluginId,
      level: 'error',
      message: `Manifest error: ${error}`,
      context: 'manifest-validation',
      detail,
      timestamp: new Date().toISOString(),
    };

    set((state) => {
      const channels = new Map(state.channels);
      const channel = { ...(channels.get(pluginId) ?? createEmptyChannel(pluginId)) };
      channel.manifestErrors = trimLogs([...channel.manifestErrors, entry]);
      channel.errors = trimLogs([...channel.errors, entry]);
      channel.logs = trimLogs([...channel.logs, entry]);
      channels.set(pluginId, channel);
      return { channels };
    });
  },

  recordPerfMetric(pluginId, metric, durationMs, detail) {
    get().appendLog(
      pluginId,
      'perf',
      `${metric}: ${durationMs.toFixed(1)}ms`,
      'performance',
      { metric, durationMs, ...detail },
    );
  },

  getChannel(pluginId) {
    return get().channels.get(pluginId) ?? createEmptyChannel(pluginId);
  },

  getAllChannels() {
    return Array.from(get().channels.values());
  },

  getLogs(pluginId, level, limit = 100) {
    const channel = get().channels.get(pluginId);
    if (!channel) return [];
    const entries = level
      ? channel.logs.filter((l) => l.level === level)
      : channel.logs;
    return entries.slice(-limit);
  },

  clearPlugin(pluginId) {
    set((state) => {
      const channels = new Map(state.channels);
      channels.delete(pluginId);
      return { channels };
    });
  },

  reset() {
    set({ channels: new Map() });
  },
}));
